import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  AccreditationDirection,
  AccreditationPrivilegeLevel,
  DiplomaticAccreditation,
  DiplomaticMission,
  Page,
  VipLoungeRequest,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import {
  Detail,
  EmptyState,
  ErrorNote,
  Field,
  Modal,
  PageHeader,
  RoleNotice,
  Spinner,
  StatusBadge,
  TabStrip,
} from '../../ui/components';

const DIRECTIONS: AccreditationDirection[] = ['OUTBOUND', 'INBOUND'];
const PRIVILEGE_LEVELS: AccreditationPrivilegeLevel[] = ['FULL_DIPLOMATIC', 'CONSULAR', 'ADMIN_TECHNICAL', 'SERVICE_STAFF'];
const NIN_PATTERN = /^\d{3}-\d{4}-\d-\d-\d{2}$/;

const TABS = [
  { key: 'accreditations', label: 'Diplomatic accreditation' },
  { key: 'vip', label: 'VIP-lounge requests' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** Protocol desk: diplomatic accreditation workflow + VIP-lounge requests — both PROTOCOL_OFFICER/MISSION_HEAD territory (M42 spec groups them together). */
export function ProtocolDesk() {
  const { persona } = useAuth();
  const isProtocolOfficer = persona?.roles.includes('PROTOCOL_OFFICER') ?? false;
  const isMissionHead = persona?.roles.includes('MISSION_HEAD') ?? false;
  const canRequest = isProtocolOfficer || isMissionHead;
  const [tab, setTab] = useState<TabKey>('accreditations');

  const missionsQ = useApiQuery<Page<DiplomaticMission>>('/api/v1/m42/missions?limit=100&status=active');
  const missions = useMemo(() => missionsQ.data?.items ?? [], [missionsQ.data]);

  return (
    <div>
      <PageHeader
        title="Protocol Desk"
        subtitle="Diplomatic accreditation lifecycle and VIP-lounge requests for visiting dignitaries."
      />

      {!canRequest ? <RoleNotice roles={['PROTOCOL_OFFICER', 'MISSION_HEAD']} /> : null}
      {missionsQ.error ? <ErrorNote message={missionsQ.error} /> : null}

      <TabStrip tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'accreditations' ? (
        <AccreditationsPanel missions={missions} isProtocolOfficer={isProtocolOfficer} canRequest={canRequest} />
      ) : (
        <VipLoungePanel missions={missions} isProtocolOfficer={isProtocolOfficer} canRequest={canRequest} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Diplomatic accreditation
// ═══════════════════════════════════════════════════════════════════════

function AccreditationsPanel({
  missions,
  isProtocolOfficer,
  canRequest,
}: {
  missions: DiplomaticMission[];
  isProtocolOfficer: boolean;
  canRequest: boolean;
}) {
  const [status, setStatus] = useState('');
  const [missionId, setMissionId] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (missionId) params.set('missionId', missionId);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m42/accreditations?${params.toString()}`;
  }, [status, missionId, appliedQ]);

  const listQ = useApiQuery<Page<DiplomaticAccreditation>>(path);
  const missionByw = useMemo(() => new Map(missions.map((m) => [m.id, m])), [missions]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-44" placeholder="Staff name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={missionId} onChange={(e) => setMissionId(e.target.value)}>
            <option value="">All missions</option>
            {missions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.code}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            <option value="REQUESTED">Requested</option>
            <option value="VETTED">Vetted</option>
            <option value="ACCREDITED">Accredited</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="WITHDRAWN">Withdrawn</option>
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
        {canRequest ? (
          <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
            + Request accreditation
          </button>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No accreditation records match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Staff</th>
                <th className="table-head">Mission</th>
                <th className="table-head">Direction</th>
                <th className="table-head">Rank</th>
                <th className="table-head">Privilege</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-medium">{a.staffName}</td>
                  <td className="table-cell font-mono text-xs">{missionByw.get(a.missionId)?.code ?? a.missionId}</td>
                  <td className="table-cell text-xs">{a.direction}</td>
                  <td className="table-cell text-xs text-slate-500">{a.rank}</td>
                  <td className="table-cell text-xs">{a.privilegeLevel.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <AccreditationDetailModal
          id={selectedId}
          missionByw={missionByw}
          isProtocolOfficer={isProtocolOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateAccreditationModal
          missions={missions}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AccreditationDetailModal({
  id,
  missionByw,
  isProtocolOfficer,
  onClose,
  onChanged,
}: {
  id: string;
  missionByw: Map<string, DiplomaticMission>;
  isProtocolOfficer: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<DiplomaticAccreditation>(`/api/v1/m42/accreditations/${id}`);
  const a = detailQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vettingNotes, setVettingNotes] = useState('');
  const [reason, setReason] = useState('');
  const [showVet, setShowVet] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={a ? `${a.staffName} — accreditation` : 'Accreditation'} onClose={onClose} wide>
      {detailQ.error ? <ErrorNote message={detailQ.error} /> : null}
      {detailQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={a.state} />} />
            <Detail label="Mission" value={missionByw.get(a.missionId)?.name ?? a.missionId} />
            <Detail label="Direction" value={a.direction} />
            <Detail label="Rank" value={a.rank} />
            <Detail label="Privilege level" value={a.privilegeLevel.replaceAll('_', ' ')} />
            <Detail label="Nationality" value={a.nationality ?? '—'} />
            {a.staffNin ? <Detail label="Staff NIN" value={a.staffNin} mono /> : null}
            {a.idCardNo ? <Detail label="ID card no." value={a.idCardNo} mono /> : null}
            {a.docVaultSerial ? <Detail label="DocVault serial" value={a.docVaultSerial} mono /> : null}
            {a.accreditedFrom ? <Detail label="Accredited from" value={formatDate(a.accreditedFrom)} /> : null}
          </div>

          {a.vettingNotes ? (
            <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600">
              <span className="font-semibold">Vetting notes:</span> {a.vettingNotes}
            </div>
          ) : null}
          {a.state === 'SUSPENDED' && a.suspendedReason ? <ErrorNote message={`Suspended: ${a.suspendedReason}`} /> : null}
          {a.state === 'WITHDRAWN' && a.withdrawnReason ? <ErrorNote message={`Withdrawn: ${a.withdrawnReason}`} /> : null}

          {isProtocolOfficer ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="flex flex-wrap gap-2">
                {a.state === 'REQUESTED' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => setShowVet((s) => !s)}>
                    Vet
                  </button>
                ) : null}
                {a.state === 'VETTED' ? (
                  <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/accreditations/${a.id}/accredit`))}>
                    {busy ? 'Accrediting…' : 'Accredit — issue DocVault card'}
                  </button>
                ) : null}
                {a.state === 'ACCREDITED' ? (
                  <button className="btn-danger" disabled={busy} onClick={() => setShowSuspend((s) => !s)}>
                    Suspend
                  </button>
                ) : null}
                {a.state === 'SUSPENDED' ? (
                  <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/accreditations/${a.id}/reinstate`))}>
                    {busy ? 'Reinstating…' : 'Reinstate'}
                  </button>
                ) : null}
                {a.state !== 'WITHDRAWN' ? (
                  <button className="btn-secondary" disabled={busy} onClick={() => setShowWithdraw((s) => !s)}>
                    Withdraw
                  </button>
                ) : null}
              </div>

              {showVet ? (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <Field label="Vetting notes">
                    <textarea className="field-input" rows={2} value={vettingNotes} onChange={(e) => setVettingNotes(e.target.value)} />
                  </Field>
                  <button
                    className="btn-primary"
                    disabled={busy || vettingNotes.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/accreditations/${a.id}/vet`, { notes: vettingNotes }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm vetted'}
                  </button>
                </div>
              ) : null}
              {showSuspend ? (
                <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <Field label="Suspension reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/accreditations/${a.id}/suspend`, { reason }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm suspend'}
                  </button>
                </div>
              ) : null}
              {showWithdraw ? (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <Field label="Withdrawal reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/accreditations/${a.id}/withdraw`, { reason }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm withdraw'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={['PROTOCOL_OFFICER']} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Requested {formatDateTime(a.createdAt)} · last updated {formatDateTime(a.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateAccreditationModal({
  missions,
  onClose,
  onDone,
}: {
  missions: DiplomaticMission[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [missionId, setMissionId] = useState(missions[0]?.id ?? '');
  const [direction, setDirection] = useState<AccreditationDirection>('OUTBOUND');
  const [staffName, setStaffName] = useState('');
  const [staffNin, setStaffNin] = useState('');
  const [nationality, setNationality] = useState('');
  const [rank, setRank] = useState('');
  const [privilegeLevel, setPrivilegeLevel] = useState<AccreditationPrivilegeLevel>('CONSULAR');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ninInvalid = staffNin.trim().length > 0 && !NIN_PATTERN.test(staffNin.trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m42/accreditations', {
        missionId,
        direction,
        staffName,
        staffNin: staffNin.trim() || undefined,
        nationality: nationality.trim() || undefined,
        rank,
        privilegeLevel,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to request accreditation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request diplomatic accreditation" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mission">
            <select className="field-input" value={missionId} onChange={(e) => setMissionId(e.target.value)}>
              {missions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Direction">
            <select className="field-input" value={direction} onChange={(e) => setDirection(e.target.value as AccreditationDirection)}>
              {DIRECTIONS.map((d) => (
                <option key={d} value={d}>
                  {d === 'OUTBOUND' ? 'Outbound — Seychellois staff posted abroad' : 'Inbound — foreign staff accredited to Seychelles'}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Staff full name">
          <input className="field-input" value={staffName} onChange={(e) => setStaffName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rank / position">
            <input className="field-input" value={rank} onChange={(e) => setRank(e.target.value)} placeholder="e.g. First Secretary (Consular)" />
          </Field>
          <Field label="Privilege level">
            <select className="field-input" value={privilegeLevel} onChange={(e) => setPrivilegeLevel(e.target.value as AccreditationPrivilegeLevel)}>
              {PRIVILEGE_LEVELS.map((p) => (
                <option key={p} value={p}>
                  {p.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={direction === 'OUTBOUND' ? 'Staff NIN' : 'Staff NIN (rarely known for foreign diplomats)'}>
            <input className="field-input font-mono" value={staffNin} onChange={(e) => setStaffNin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
            {ninInvalid ? <p className="mt-1 text-xs text-rose-600">Expected format NNN-NNNN-N-N-NN.</p> : null}
          </Field>
          <Field label="Nationality (mainly for inbound)">
            <input className="field-input" value={nationality} onChange={(e) => setNationality(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !missionId || staffName.trim().length < 2 || rank.trim().length < 2 || ninInvalid}
          >
            {busy ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VIP-lounge requests
// ═══════════════════════════════════════════════════════════════════════

function VipLoungePanel({
  missions,
  isProtocolOfficer,
  canRequest,
}: {
  missions: DiplomaticMission[];
  isProtocolOfficer: boolean;
  canRequest: boolean;
}) {
  const [status, setStatus] = useState('');
  const [missionId, setMissionId] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (missionId) params.set('missionId', missionId);
    return `/api/v1/m42/vip-lounge-requests?${params.toString()}`;
  }, [status, missionId]);

  const listQ = useApiQuery<Page<VipLoungeRequest>>(path);
  const missionByw = useMemo(() => new Map(missions.map((m) => [m.id, m])), [missions]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className="field-input !w-auto" value={missionId} onChange={(e) => setMissionId(e.target.value)}>
          <option value="">All missions</option>
          {missions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.code}
            </option>
          ))}
        </select>
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          <option value="REQUESTED">Requested</option>
          <option value="APPROVED">Approved</option>
          <option value="DECLINED">Declined</option>
          <option value="COMPLETED">Completed</option>
        </select>
        {canRequest ? (
          <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
            + New VIP-lounge request
          </button>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No VIP-lounge requests match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Traveller</th>
                <th className="table-head">Mission</th>
                <th className="table-head">Visit date</th>
                <th className="table-head">Purpose</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(v.id)}>
                  <td className="table-cell font-medium">{v.travellerName}</td>
                  <td className="table-cell font-mono text-xs">{missionByw.get(v.missionId)?.code ?? v.missionId}</td>
                  <td className="table-cell">{formatDate(v.visitDate)}</td>
                  <td className="table-cell text-xs text-slate-500">{v.purpose}</td>
                  <td className="table-cell">
                    <StatusBadge status={v.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <VipLoungeDetailModal
          id={selectedId}
          missionByw={missionByw}
          isProtocolOfficer={isProtocolOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateVipLoungeModal
          missions={missions}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function VipLoungeDetailModal({
  id,
  missionByw,
  isProtocolOfficer,
  onClose,
  onChanged,
}: {
  id: string;
  missionByw: Map<string, DiplomaticMission>;
  isProtocolOfficer: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<VipLoungeRequest>(`/api/v1/m42/vip-lounge-requests/${id}`);
  const v = detailQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={v ? `${v.travellerName} — VIP lounge` : 'VIP-lounge request'} onClose={onClose} wide>
      {detailQ.error ? <ErrorNote message={detailQ.error} /> : null}
      {detailQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {v ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={v.state} />} />
            <Detail label="Mission" value={missionByw.get(v.missionId)?.name ?? v.missionId} />
            <Detail label="Visit date" value={formatDate(v.visitDate)} />
            <Detail label="Purpose" value={v.purpose} />
            {v.travellerDetails?.flightNo ? <Detail label="Flight" value={String(v.travellerDetails.flightNo)} /> : null}
            {v.travellerDetails?.partySize ? <Detail label="Party size" value={String(v.travellerDetails.partySize)} /> : null}
            {v.travellerDetails?.arrivalTime ? <Detail label="Arrival" value={formatDateTime(String(v.travellerDetails.arrivalTime))} /> : null}
          </div>

          {v.state === 'DECLINED' && v.declineReason ? <ErrorNote message={`Declined: ${v.declineReason}`} /> : null}

          {isProtocolOfficer ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="flex flex-wrap gap-2">
                {v.state === 'REQUESTED' ? (
                  <>
                    <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/vip-lounge-requests/${v.id}/approve`))}>
                      {busy ? 'Approving…' : 'Approve'}
                    </button>
                    <button className="btn-danger" disabled={busy} onClick={() => setShowDecline((s) => !s)}>
                      Decline
                    </button>
                  </>
                ) : null}
                {v.state === 'APPROVED' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/vip-lounge-requests/${v.id}/complete`))}>
                    {busy ? 'Completing…' : 'Mark completed'}
                  </button>
                ) : null}
              </div>
              {showDecline ? (
                <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <Field label="Decline reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/vip-lounge-requests/${v.id}/decline`, { reason }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm decline'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={['PROTOCOL_OFFICER']} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Requested {formatDateTime(v.createdAt)} · last updated {formatDateTime(v.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateVipLoungeModal({
  missions,
  onClose,
  onDone,
}: {
  missions: DiplomaticMission[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [missionId, setMissionId] = useState(missions[0]?.id ?? '');
  const [travellerName, setTravellerName] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [flightNo, setFlightNo] = useState('');
  const [partySize, setPartySize] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const travellerDetails: Record<string, unknown> = {};
      if (flightNo.trim()) travellerDetails.flightNo = flightNo.trim();
      if (partySize.trim()) travellerDetails.partySize = Number.parseInt(partySize, 10);
      await api.post('/api/v1/m42/vip-lounge-requests', {
        missionId,
        travellerName,
        visitDate,
        purpose,
        travellerDetails: Object.keys(travellerDetails).length > 0 ? travellerDetails : undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to request VIP-lounge access');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New VIP-lounge request" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Mission">
          <select className="field-input" value={missionId} onChange={(e) => setMissionId(e.target.value)}>
            {missions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.code})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Traveller / delegation name">
          <input className="field-input" value={travellerName} onChange={(e) => setTravellerName(e.target.value)} placeholder="e.g. Hon. Minister Delegation" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Visit date">
            <input type="date" className="field-input" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />
          </Field>
          <Field label="Party size (optional)">
            <input type="number" min="1" className="field-input" value={partySize} onChange={(e) => setPartySize(e.target.value)} />
          </Field>
        </div>
        <Field label="Flight number (optional)">
          <input className="field-input" value={flightNo} onChange={(e) => setFlightNo(e.target.value)} placeholder="e.g. EK203" />
        </Field>
        <Field label="Purpose of visit">
          <textarea className="field-input" rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !missionId || travellerName.trim().length < 2 || !visitDate || purpose.trim().length < 3}
          >
            {busy ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
